"use client";

/**
 * Global error boundary (Next.js App Router convention).
 *
 * Catches uncaught render errors from any route segment and shows a recovery
 * UI instead of leaving the user with a blank page. For React hook-order
 * errors (#300, #310) the fiber tree is corrupt and re-rendering is unsafe,
 * so we suppress the "Try again" button and force a full reload.
 */

import { useEffect } from "react";

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error("[app/error.tsx] Unhandled render error:", error);
  }, [error]);

  const isHookError = /Minified React error #(300|310)|Rendered fewer hooks/.test(
    error.message,
  );

  return (
    <div
      style={{
        padding: 24,
        fontFamily: "system-ui, sans-serif",
        maxWidth: 640,
        margin: "48px auto",
      }}
    >
      <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>
        Something went wrong
      </h2>
      <p style={{ marginBottom: 12, color: "#444" }}>
        {isHookError
          ? "The app hit an internal rendering error and needs to reload."
          : error.message}
      </p>
      {error.digest && (
        <p style={{ color: "#666", fontSize: 12, marginBottom: 16 }}>
          Error ID: {error.digest}
        </p>
      )}
      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            padding: "6px 14px",
            border: "1px solid #ccc",
            borderRadius: 6,
            background: "#f46a2b",
            color: "white",
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          Reload
        </button>
        {!isHookError && (
          <button
            type="button"
            onClick={() => reset()}
            style={{
              padding: "6px 14px",
              border: "1px solid #ccc",
              borderRadius: 6,
              background: "white",
              cursor: "pointer",
            }}
          >
            Try again
          </button>
        )}
      </div>
    </div>
  );
}
